
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/common/Layout';

// Public pages
import Home from '../pages/public/Home';
import Login from '../pages/public/Login';
import Notices from '../pages/public/Notices';
import NoticeDetail from '../pages/public/NoticeDetail';
import About from '../pages/public/About';
import Admission from '../pages/public/Admission';
import Teachers from '../pages/public/Teachers';
import Results from '../pages/public/Results';
import Gallery from '../pages/public/Gallery';
import Contact from '../pages/public/Contact';
import ForgotPassword from '../pages/public/ForgotPassword';

// Student pages
import StudentDashboard from '../pages/student/Dashboard';
import StudentProfile from '../pages/student/Profile';
import StudentAttendance from '../pages/student/Attendance';
import StudentResults from '../pages/student/Results';
import StudentAdmitCard from '../pages/student/AdmitCard';
import StudentChangePassword from '../pages/student/ChangePassword';

// Teacher pages
import TeacherDashboard from '../pages/teacher/Dashboard';
import TeacherProfile from '../pages/teacher/Profile';
import TeacherClasses from '../pages/teacher/Classes';
import TeacherStudents from '../pages/teacher/Students';
import TeacherAttendance from '../pages/teacher/Attendance';
import TeacherMarks from '../pages/teacher/Marks';
import TeacherMaterials from '../pages/teacher/Materials';
import TeacherChangePassword from '../pages/teacher/ChangePassword';

// Admin pages
import AdminDashboard from '../pages/admin/Dashboard';
import NoticeList from '../pages/admin/notices/NoticeList';
import CreateNotice from '../pages/admin/notices/CreateNotice';
import EditNotice from '../pages/admin/notices/EditNotice';
import StudentList from '../pages/admin/students/StudentList';
import AddStudent from '../pages/admin/students/AddStudent';
import EditStudent from '../pages/admin/students/EditStudent';
import ViewStudent from '../pages/admin/students/ViewStudent';
import TeacherList from '../pages/admin/teachers/TeacherList';
import AddTeacher from '../pages/admin/teachers/AddTeacher';
import EditTeacher from '../pages/admin/teachers/EditTeacher';
import ResultList from '../pages/admin/results/ResultList';
import UploadResult from '../pages/admin/results/UploadResult';
import EditResult from '../pages/admin/results/EditResult';
import AdmissionList from '../pages/admin/admissions/AdmissionList';
import ViewApplication from '../pages/admin/admissions/ViewApplication';
import GalleryList from '../pages/admin/gallery/GalleryList';
import UploadPhoto from '../pages/admin/gallery/UploadPhoto';
import UserList from '../pages/admin/users/UserList';
import ManageRoles from '../pages/admin/users/ManageRoles';
import Settings from '../pages/admin/settings/Settings';

// Private Route Component
const PrivateRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const RoutesConfig = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/notices" element={<Notices />} />
      <Route path="/notices/:id" element={<NoticeDetail />} />
      <Route path="/about" element={<About />} />
      <Route path="/admission" element={<Admission />} />
      <Route path="/teachers" element={<Teachers />} />
      <Route path="/results" element={<Results />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Student Routes */}
      <Route path="/student" element={
        <PrivateRoute allowedRoles={['student']}>
          <Layout />
        </PrivateRoute>
      }>
        <Route path="dashboard" element={<StudentDashboard />} />
        <Route path="profile" element={<StudentProfile />} />
        <Route path="attendance" element={<StudentAttendance />} />
        <Route path="results" element={<StudentResults />} />
        <Route path="admit-card" element={<StudentAdmitCard />} />
        <Route path="change-password" element={<StudentChangePassword />} />
      </Route>

      {/* Teacher Routes */}
      <Route path="/teacher" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <Layout />
        </PrivateRoute>
      }>
        <Route path="dashboard" element={<TeacherDashboard />} />
        <Route path="profile" element={<TeacherProfile />} />
        <Route path="classes" element={<TeacherClasses />} />
        <Route path="students" element={<TeacherStudents />} />
        <Route path="attendance" element={<TeacherAttendance />} />
        <Route path="marks" element={<TeacherMarks />} />
        <Route path="materials" element={<TeacherMaterials />} />
        <Route path="change-password" element={<TeacherChangePassword />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Layout />
        </PrivateRoute>
      }>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="notices" element={<NoticeList />} />
        <Route path="notices/create" element={<CreateNotice />} />
        <Route path="notices/edit/:id" element={<EditNotice />} />
        <Route path="students" element={<StudentList />} />
        <Route path="students/add" element={<AddStudent />} />
        <Route path="students/edit/:id" element={<EditStudent />} />
        <Route path="students/view/:id" element={<ViewStudent />} />
        <Route path="teachers" element={<TeacherList />} />
        <Route path="teachers/add" element={<AddTeacher />} />
        <Route path="teachers/edit/:id" element={<EditTeacher />} />
        <Route path="results" element={<ResultList />} />
        <Route path="results/upload" element={<UploadResult />} />
        <Route path="results/edit/:id" element={<EditResult />} />
        <Route path="admissions" element={<AdmissionList />} />
        <Route path="admissions/view/:id" element={<ViewApplication />} />
        <Route path="gallery" element={<GalleryList />} />
        <Route path="gallery/upload" element={<UploadPhoto />} />
        <Route path="users" element={<UserList />} />
        <Route path="users/manage-roles" element={<ManageRoles />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default RoutesConfig;